import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Container, 
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  Skeleton,
  Fade,
  useMediaQuery,
  useTheme,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { registerApi } from '../api/auth';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';

const Register = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Initial loading effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 25;
    return strength;
  };

  // Individual validation functions
  const validateFirstName = (value) => {
    if (!value || value.trim().length === 0) return 'First name is required';
    if (value.trim().length < 2) return 'First name must be at least 2 characters';
    return null;
  };

  const validateLastName = (value) => {
    if (!value || value.trim().length === 0) return 'Last name is required';
    if (value.trim().length < 2) return 'Last name must be at least 2 characters';
    return null;
  };

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return null;
  };

  const validatePassword = (value) => {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    return null;
  };

  const validateConfirmPassword = (value, passwordValue) => {
    if (!value) return 'Please confirm your password';
    if (value !== passwordValue) return 'Passwords do not match';
    return null;
  };

  const validateCompany = (value) => {
    if (!value || value.trim().length === 0) return 'Company name is required';
    if (value.trim().length < 2) return 'Company name must be at least 2 characters';
    return null;
  };

  // Real-time validation
  const validateField = (field, value, extraValue = null) => {
    let error = null;
    switch (field) {
      case 'firstName':
        error = validateFirstName(value);
        break;
      case 'lastName':
        error = validateLastName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(value, extraValue || password);
        break;
      case 'company':
        error = validateCompany(value);
        break;
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    return error === null;
  };

  const handleFieldChange = (field, value) => {
    switch (field) {
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        setPasswordStrength(calculatePasswordStrength(value));
        // Also revalidate confirm password if it's been touched
        if (touched.confirmPassword) {
          validateField('confirmPassword', confirmPassword, value);
        }
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
      case 'company':
        setCompany(value);
        break;
      default:
        break;
    }
    
    // Validate on change if field has been touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleFieldBlur = (field, value) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  const validateForm = () => {
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const confirmPasswordError = validateConfirmPassword(confirmPassword, password);
    const companyError = validateCompany(company);
    
    setValidationErrors({
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
      company: companyError
    });
    
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      password: true,
      confirmPassword: true,
      company: true
    });
    
    return !firstNameError && !lastNameError && !emailError && !passwordError && !confirmPasswordError && !companyError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!validateForm()) {
      enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
      return;
    }
    
    setLoading(true);
    
    try {
      const data = await registerApi({ firstName, lastName, email, password, company });
      console.log(data);
      
      if (data?.status !== 'success') {
        throw new Error(data?.message || 'Registration failed');
      }
      
      setSuccess('Account created successfully! Please check your email to verify your account. You may log in after admin approval.');
      enqueueSnackbar('Account created successfully!', { variant: 'success' });
      
      // Reset form on success
      setTimeout(() => {
        enqueueSnackbar('Redirecting to sign in...', { variant: 'info' });
        navigate('/signin');
      }, 3000);
      
    } catch (e) {
      const errorMessage = e?.response?.data?.message || e?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  if (isInitialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          width: '40%',
          background: 'linear-gradient(135deg, #0FABDB 0%, #10C4DB 100%)',
          p: 4
        }}>
          <Skeleton variant="rectangular" width="60%" height={120} sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
            <Skeleton variant="text" width="80%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Skeleton variant="text" width="60%" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Box>
          <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Container maxWidth="sm">
            <Box sx={{ p: { xs: 3, sm: 5 } }}>
              <Skeleton variant="text" width="70%" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="50%" height={20} sx={{ mb: 4 }} />
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Skeleton variant="rectangular" width="48%" height={56} sx={{ borderRadius: 1.5 }} />
                <Skeleton variant="rectangular" width="48%" height={56} sx={{ borderRadius: 1.5 }} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3, borderRadius: 1.5 }} />
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3, borderRadius: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Skeleton variant="rectangular" width="48%" height={56} sx={{ borderRadius: 1.5 }} />
                <Skeleton variant="rectangular" width="48%" height={56} sx={{ borderRadius: 1.5 }} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1.5 }} />
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Fade in={!isInitialLoading} timeout={800}>
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        {/* Left Side - Gradient Background with Logo */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ display: isMobile ? 'none' : 'flex', width: '40%' }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            background: 'linear-gradient(135deg, #0FABDB 0%, #10C4DB 100%)',
            p: 4,
            color: 'white',
            justifyContent: 'space-between'
          }}>
        {/* Logo Area */}
        <Box sx={{ mt: 6, ml: 4,mb: 8 }}>
          <Box 
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 2, 
              py: .5, 
              px: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 'fit-content'
            }}
          >
            <Typography 
              variant="h2" 
              component="div" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#12C1DC',
                letterSpacing: 1,
                fontSize: '6rem'
              }}
            >
              APEX
            </Typography>
            <Typography 
              variant="subtitle1" 
              component="div" 
              sx={{ 
                color: '#12C1DC',
                letterSpacing: 3.5,
                textTransform: 'uppercase',
                fontSize: '1.5rem',
                mt: -1.5
              }}
            >
              PAYROLL SYSTEM
            </Typography>
          </Box>
        </Box>
        
        {/* Welcome Text */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, ml: 4 }}>
          <Typography variant="h4" fontWeight="500" sx={{ mb: 1, fontSize: '2rem' }}>
            Welcome to APEX!
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            Applicant Employee Experience
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 'auto', fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>© {currentYear} Sparkle Star International Corporation</Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Link href="#" color="inherit">Privacy</Link>
            <Link href="#" color="inherit">Legal</Link>
            <Link href="#" color="inherit">Contact</Link>
          </Box>
          </Box>
          </Box>
        </motion.div>

        {/* Right Side - Register Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ flex: 1 }}
        >
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, sm: 4 },
            py: { xs: 4, sm: 2 },
            bgcolor: 'white',
            minHeight: '100vh',
            overflowY: 'auto'
          }}>
            <Container maxWidth="sm">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Box 
                  component="form" 
                  onSubmit={handleSubmit}
                  sx={{ 
                    bgcolor: 'white',
                    borderRadius: 2,
                    p: { xs: 3, sm: 5 },
                    boxShadow: { xs: '0px 4px 20px rgba(0, 0, 0, 0.05)', md: 'none' },
                    maxWidth: '100%'
                  }}
                >
                  <Box sx={{ textAlign: 'left', mb: 4 }}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                    >
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        component="h1" 
                        fontWeight="500" 
                        color="text.primary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      >
                        <PersonAddIcon sx={{ color: '#12C1DC', fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                        Create your <Typography component="span" color="#12C1DC" variant={isMobile ? "h6" : "h5"} fontWeight="bold">APEX</Typography> account
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Enter your details to get started
                      </Typography>
                    </motion.div>
                  </Box>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    </motion.div>
                  )}
                  {success && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Alert 
                        severity="success" 
                        sx={{ mb: 2 }}
                        icon={<CheckCircleIcon />}
                      >
                        {success}
                      </Alert>
                    </motion.div>
                  )}

                  {/* Name Fields */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                          First name
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Enter your first name"
                          variant="outlined"
                          value={firstName}
                          onChange={(e) => handleFieldChange('firstName', e.target.value)}
                          onBlur={(e) => handleFieldBlur('firstName', e.target.value)}
                          error={touched.firstName && !!validationErrors.firstName}
                          helperText={touched.firstName && validationErrors.firstName}
                          disabled={loading}
                          InputProps={{
                            sx: { 
                              borderRadius: 1.5,
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#35CFF7',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#12C1DC',
                              }
                            }
                          }}
                          FormHelperTextProps={{
                            sx: { 
                              mx: 0, 
                              mt: 0.5,
                              fontSize: '0.75rem'
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                          Last name
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Enter your last name"
                          variant="outlined"
                          value={lastName}
                          onChange={(e) => handleFieldChange('lastName', e.target.value)}
                          onBlur={(e) => handleFieldBlur('lastName', e.target.value)}
                          error={touched.lastName && !!validationErrors.lastName}
                          helperText={touched.lastName && validationErrors.lastName}
                          disabled={loading}
                          InputProps={{
                            sx: { 
                              borderRadius: 1.5,
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#35CFF7',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#12C1DC',
                              }
                            }
                          }}
                          FormHelperTextProps={{
                            sx: { 
                              mx: 0, 
                              mt: 0.5,
                              fontSize: '0.75rem'
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </motion.div>

                  {/* Email Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                        Email address
                      </Typography>
                      <TextField
                        fullWidth
                        type="email"
                        placeholder="Enter your email address"
                        variant="outlined"
                        value={email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        onBlur={(e) => handleFieldBlur('email', e.target.value)}
                        error={touched.email && !!validationErrors.email}
                        helperText={touched.email && validationErrors.email}
                        disabled={loading}
                        InputProps={{
                          sx: { 
                            borderRadius: 1.5,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#35CFF7',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#12C1DC',
                            }
                          }
                        }}
                        FormHelperTextProps={{
                          sx: { 
                            mx: 0, 
                            mt: 0.5,
                            fontSize: '0.75rem'
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }
                        }}
                      />
                    </Box>
                  </motion.div>

                  {/* Company Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                        Company name
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter your company name"
                        variant="outlined"
                        value={company}
                        onChange={(e) => handleFieldChange('company', e.target.value)}
                        onBlur={(e) => handleFieldBlur('company', e.target.value)}
                        error={touched.company && !!validationErrors.company}
                        helperText={touched.company && validationErrors.company}
                        disabled={loading}
                        InputProps={{
                          sx: { 
                            borderRadius: 1.5,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#35CFF7',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#12C1DC',
                            }
                          }
                        }}
                        FormHelperTextProps={{
                          sx: { 
                            mx: 0, 
                            mt: 0.5,
                            fontSize: '0.75rem'
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }
                        }}
                      />
                    </Box>
                  </motion.div>

                  {/* Password Fields */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                          Password
                        </Typography>
                        <TextField
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a password"
                          variant="outlined"
                          value={password}
                          onChange={(e) => handleFieldChange('password', e.target.value)}
                          onBlur={(e) => handleFieldBlur('password', e.target.value)}
                          error={touched.password && !!validationErrors.password}
                          helperText={touched.password && validationErrors.password}
                          disabled={loading}
                          InputProps={{
                            sx: { 
                              borderRadius: 1.5,
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#35CFF7',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#12C1DC',
                              }
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                                  <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    disabled={loading}
                                    sx={{ 
                                      color: '#666',
                                      '&:hover': {
                                        color: '#12C1DC'
                                      }
                                    }}
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: { 
                              mx: 0, 
                              mt: 0.5,
                              fontSize: '0.75rem'
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }
                          }}
                        />
                        {/* Password Strength Indicator */}
                        {password && (
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Strength:
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={passwordStrength} 
                                sx={{ 
                                  flex: 1, 
                                  height: 4, 
                                  borderRadius: 2,
                                  backgroundColor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: passwordStrength < 50 ? '#f44336' : passwordStrength < 75 ? '#ff9800' : '#4caf50'
                                  }
                                }} 
                              />
                              <Typography variant="caption" color={passwordStrength < 50 ? 'error' : passwordStrength < 75 ? 'warning.main' : 'success.main'}>
                                {passwordStrength < 50 ? 'Weak' : passwordStrength < 75 ? 'Medium' : 'Strong'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 1 }}>
                          Confirm password
                        </Typography>
                        <TextField
                          fullWidth
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          variant="outlined"
                          value={confirmPassword}
                          onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                          onBlur={(e) => handleFieldBlur('confirmPassword', e.target.value)}
                          error={touched.confirmPassword && !!validationErrors.confirmPassword}
                          helperText={touched.confirmPassword && validationErrors.confirmPassword}
                          disabled={loading}
                          InputProps={{
                            sx: { 
                              borderRadius: 1.5,
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#35CFF7',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#12C1DC',
                              }
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={showConfirmPassword ? "Hide password" : "Show password"}>
                                  <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    disabled={loading}
                                    sx={{ 
                                      color: '#666',
                                      '&:hover': {
                                        color: '#12C1DC'
                                      }
                                    }}
                                  >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: { 
                              mx: 0, 
                              mt: 0.5,
                              fontSize: '0.75rem'
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.1 }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      startIcon={!loading ? <PersonAddIcon /> : null}
                      sx={{
                        bgcolor: '#12C1DC',
                        py: { xs: 1.2, md: 1.5 },
                        textTransform: 'none',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 'medium',
                        '&:hover': {
                          bgcolor: '#0FABDB',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(18, 193, 220, 0.4)'
                        },
                        '&:disabled': {
                          bgcolor: '#ccc',
                          color: 'white'
                        },
                        borderRadius: 1.5,
                        position: 'relative',
                        minHeight: { xs: '44px', md: '48px' },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      disabled={loading || Object.values(validationErrors).some(error => error !== null)}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: 'white' }} />
                          <Typography variant="button" sx={{ textTransform: 'none' }}>
                            Creating account...
                          </Typography>
                        </Box>
                      ) : (
                        'Create account'
                      )}
                    </Button>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.2 }}
                  >
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                        Already have an account?{' '}
                        <Link 
                          component={RouterLink}
                          to="/signin" 
                          sx={{ 
                            color: loading ? '#ccc' : '#12C1DC', 
                            textDecoration: 'none',
                            pointerEvents: loading ? 'none' : 'auto',
                            fontWeight: 'medium',
                            '&:hover': {
                              textDecoration: loading ? 'none' : 'underline'
                            }
                          }}
                        >
                          Log in
                        </Link>
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </motion.div>
            </Container>
          </Box>
        </motion.div>
      </Box>
    </Fade>
  );
};

export default Register;


